import React from 'react';
import {
	BearingInfo,
	BearingLubricationResult,
	BearingLubricationSpeedsResponseNonTRB,
	BearingTypeCode,
	TimkenUnit
} from '../../components/bearing-search/types/types';

import {lubricationSpeedUnitMap} from '../data';

export type FormState = {
	lubricationType: string;
	operatingSpeed: string;
	partNumber: string;
	units: TimkenUnit;
}

export const initialFormState: FormState = {
	partNumber: '',
	units: 'M',
	operatingSpeed: '',
	lubricationType: '',
};

export type BearingPartState = {
	bearingSubType?: string;
	bearingType?: BearingTypeCode | '';
	cupPartNumber?: string;
	innerRingPartNumber?: string | string[] | '';
	isSingleRow?: boolean;
	marketPlaceDescription?: string;
	outerRingPartNumber?: string | string[] | '';
	partNumber?: string;
	typeDescription?: string;
	speedUnit?: string;
}

export const initialBearingPartState: BearingPartState = {
	innerRingPartNumber: '',
	marketPlaceDescription: '',
	outerRingPartNumber: '',
	bearingType: '',
	bearingSubType: '',
	typeDescription: '',
	isSingleRow: false,
};
// this needs to be an array of objects
export type CalculationState = {
	sectionHeading: string[];
	sectionName: string;
	sectionData: string[][];
}

export const initialCalculationState: CalculationState = {
	sectionHeading: [],
	sectionName: '',
	sectionData: [],
};

export type BearingPartAction =
	| { type: 'select_part'; payload: BearingInfo }
	| { type: 'reset'; payload?: BearingPartState };

export type CalculationAction =
	| { type: 'reset'; payload?: CalculationState }
	| { type: 'update_nonTRB_calculation_data'; payload: BearingLubricationSpeedsResponseNonTRB }
	| { type: 'update_calculation_data'; payload: BearingLubricationResult[] };

export type FormStateAction =
	| { type: 'change_lubrication_type'; payload: string }
	| { type: 'change_operating_speed'; payload: string }
	| { type: 'change_part_number'; payload: string }
	| { type: 'change_unit'; payload: TimkenUnit }
	| { type: 'reset'; payload?: FormState };

const calculationReducer: React.Reducer<
	CalculationState,
	CalculationAction
> = (state, action) => {
	switch (action.type) {
		case 'update_calculation_data': {
			const results = action.payload;
			const mappedResults = mapCalculationResults(results);

			return {
				...state,
				sectionHeading: mappedResults.sectionHeading,
				sectionName: mappedResults.sectionName,
				sectionData: mappedResults.sectionData,
			};
		}
		case 'update_nonTRB_calculation_data': {
			const results = action.payload;
			const mappedResults = mapCalculationResultsNonTRB(results);

			return {
				...state,
				sectionHeading: mappedResults.sectionHeading,
				sectionName: mappedResults.sectionName,
				sectionData: mappedResults.sectionData,
			};
		}
		case 'reset': {
			const newState = action.payload ? action.payload : initialCalculationState;
			return {
				...newState,
			};
		}
	}
	return handleActionError(action);
};

const formReducer: React.Reducer<
	FormState,
	FormStateAction
> = (state, action) => {
	switch (action.type) {
		case 'change_lubrication_type': {
			return {
				...state,
				lubricationType: action.payload,
			};
		}
		case 'change_operating_speed': {
			return {
				...state,
				operatingSpeed: action.payload,
			};
		}
		case 'change_part_number': {
			return {
				...state,
				partNumber: action.payload,
			};
		}
		case 'change_unit': {
			return {
				...state,
				units: action.payload,
			};
		}
		case 'reset': {
			const newState = action.payload ? action.payload : initialFormState;
			return {
				...newState,
			};
		}
	}
	return handleActionError(action);
};

export const handleActionError = (action: never): never => {
	throw Error(`Unknown action ${ action }`);
};

const mapCalculationResultsNonTRB = (results: BearingLubricationSpeedsResponseNonTRB) => {
	/**
	 * Check if the passed in value exists or not
	 * @param value
	 */
	const isEmptyValue = (value: any) => {
		return value === null || value === undefined || value === '' || value === 'NA';
	};

	/**
	 * If the value coming from isEmptyValue is false, don't push the columns and skip them.
	 * If the value is true, add the necessary header and targetKey data
	 *
	 * @param headingData
	 * @param targetKeys
	 * @param header
	 * @param key
	 * @param condition
	 */
	const addColumn = (
		headingData: string[],
		targetKeys: string[],
		header: string,
		key: string,
		condition: boolean = true
	) => {
		if (condition) {
			headingData.push(header);
			targetKeys.push(key);
		}
	};

	let headingData = ['Bearing Type', 'Part Number'];
	let targetKeys = ['bearingType', 'partNumber'];

// Config used to determine the proper headers and targetKeys to be added (based on bearingType)
	const bearingConfig = {
		SRB: () => {
			addColumn(headingData, targetKeys, 'Thermal Reference Speed - Oil (RPM)', 'refOilRating');
			addColumn(headingData, targetKeys, 'Thermal Reference Speed - Grease (RPM)', 'refGreaseRating');
			addColumn(headingData, targetKeys, 'Limiting Speed', 'limitingSpeed');
		},
		CRB: () => {
			addColumn(headingData, targetKeys, 'Thermal Reference Speed - Oil (RPM)', 'refOilRating');
			addColumn(headingData, targetKeys, 'Thermal Reference Speed - Grease (RPM)', 'refGreaseRating');
		},
		ACBB: () => {
			addColumn(headingData, targetKeys, 'Thermal Reference Speed - Oil (RPM)', 'refOilRating', !isEmptyValue(results.refOilRating));
			addColumn(headingData, targetKeys, 'Thermal Reference Speed - Grease (RPM)', 'refGreaseRating', !isEmptyValue(results.refGreaseRating));
			addColumn(headingData, targetKeys, 'Limiting Speed Oil (RPM)', 'limitingSpeedOil');
			addColumn(headingData, targetKeys, 'Limiting Speed Grease (RPM)', 'limitingSpeedGrease');
		},
		RBB: () => {
			addColumn(headingData, targetKeys, 'Thermal Reference Speed - Oil (RPM)', 'refOilRating', !isEmptyValue(results.refOilRating));
			addColumn(headingData, targetKeys, 'Thermal Reference Speed - Grease (RPM)', 'refGreaseRating', !isEmptyValue(results.refGreaseRating));
			addColumn(headingData, targetKeys, 'Limiting Speed Oil (RPM)', 'limitingSpeedOil', !isEmptyValue(results.limitingSpeedOil));
			addColumn(headingData, targetKeys, 'Limiting Speed Grease (RPM)', 'limitingSpeedGrease', !isEmptyValue(results.limitingSpeedGrease));
		}
	};

	const configFunction = bearingConfig[results.bearingType as keyof typeof bearingConfig];
	if (configFunction) {
		configFunction();
	}

	const mappedResultsObject: CalculationState = {
		sectionHeading: headingData,
		sectionName: '',
		sectionData: [],
	};

	const singleRowData = targetKeys.map((keyName) => {
		return results[ keyName ].toString();
	});

	mappedResultsObject.sectionData.push(singleRowData);

	return mappedResultsObject;
};

const findObjectByKey = (array: BearingLubricationResult[], key: keyof BearingLubricationResult, value: string | number) => {
	return array.find((item) => item[ key ] === value);
};

const parseCommaNumber = (value: string): number => {
	return Number(value.replace(/,/g, ''));
};

const generateDynamicHeaders = (results: BearingLubricationResult[], unit: TimkenUnit): string[] => {
	const speeds: number[] = [];

	results.forEach(result => {
		if (result.ribSpeed.includes('>')) {
			return;
		}

		const speed = parseCommaNumber(result.ribSpeed);
		if (! isNaN(speed)) {
			speeds.push(speed);
		}
	});

	const uniqueSpeeds = [ ...new Set(speeds) ].sort((a, b) => a - b);

	if (uniqueSpeeds.length === 0) {
		return [];
	}

	const maxSpeed = uniqueSpeeds[uniqueSpeeds.length - 1];
	const headerSpeeds: number[] = [];

	if (unit === 'M') {
		const roundedMax = Math.ceil(maxSpeed / 5) * 5;

		for (let speed = 5; speed <= roundedMax; speed += 5) {
			headerSpeeds.push(speed);
		}
	} else {
		const roundedMax = Math.ceil(maxSpeed / 1000) * 1000;

		for (let speed = 1000; speed <= roundedMax; speed += 1000) {
			headerSpeeds.push(speed);
		}
	}

	return headerSpeeds.map(speed => {
		if (unit === 'I') {
			return speed.toLocaleString('en-US');
		}
		return speed.toString();
	});
};

const buildAllSpeedRatingValues = (results: Record<string, BearingLubricationResult[]>, headingData: string[]) => {
	const fullResultsSet: Record<string, BearingLubricationResult[]> = {};
	const headingDataCopy = headingData.slice(1);

	for (const key in results) {
		const targetResults = structuredClone(results[key]);
		const upperLimitItem = targetResults.find(item => item.ribSpeed.includes('>'));
		let upperLimitThreshold: number | null = null;

		if (upperLimitItem) {
			const match = upperLimitItem.ribSpeed.match(/>\s*([\d,\.]+)/);
			if (match) {
				upperLimitThreshold = parseCommaNumber(match[1]);
			}
		}

		const sortedResults = targetResults
			.filter(value => ! value.ribSpeed.includes('>'))
			.sort((a, b) => {
				const speedA = parseCommaNumber(a.ribSpeed);
				const speedB = parseCommaNumber(b.ribSpeed);
				return speedA - speedB;
			});

		const completeResults: BearingLubricationResult[] = [];

		headingDataCopy.forEach((headingSpeed, index) => {
			const targetSpeed = parseCommaNumber(headingSpeed);
			const columnRangeStart = index > 0 ? parseCommaNumber(headingDataCopy[index - 1]) : 0;
			const columnRangeEnd = targetSpeed;
			const columnWidth = columnRangeEnd - columnRangeStart;

			if (upperLimitThreshold !== null && targetSpeed > upperLimitThreshold) {
				const transitionsInRange: Array<{position: number, speed: number, recommendationId: number}> = [];

				if (upperLimitThreshold > columnRangeStart) {
					const positionInColumn = ((upperLimitThreshold - columnRangeStart) / columnWidth) * 100;
					transitionsInRange.push({
						position: positionInColumn,
						speed: upperLimitThreshold,
						recommendationId: 0,
					});
				}

				const initialResult = sortedResults.find(r => parseCommaNumber(r.ribSpeed) > columnRangeStart) || sortedResults[0];

				completeResults.push({
					...sortedResults[sortedResults.length - 1],
					ribSpeed: headingSpeed,
					recommendationId: 0,
					transitions: transitionsInRange,
					initialRecommendationId: initialResult.recommendationId,
				} as any);
				return;
			}

			const transitionsInRange: Array<{position: number, speed: number, recommendationId: number}> = [];
			const initialResult = sortedResults.find(r => parseCommaNumber(r.ribSpeed) > columnRangeStart) || sortedResults[0];
			const initialRecommendationId = initialResult.recommendationId;

			sortedResults.forEach((result, resultIndex) => {
				const resultSpeed = parseCommaNumber(result.ribSpeed);

				if (resultSpeed > columnRangeStart && resultSpeed <= columnRangeEnd) {
					const positionInColumn = ((resultSpeed - columnRangeStart) / columnWidth) * 100;
					const nextResult = sortedResults[resultIndex + 1];

					if (nextResult) {
						transitionsInRange.push({
							position: positionInColumn,
							speed: resultSpeed,
							recommendationId: nextResult.recommendationId,
						});
					} else if (upperLimitThreshold !== null) {
						transitionsInRange.push({
							position: positionInColumn,
							speed: resultSpeed,
							recommendationId: 0,
						});
					}
				}
			});

			let appropriateResult = sortedResults.find(r => parseCommaNumber(r.ribSpeed) >= targetSpeed);

			if (! appropriateResult && sortedResults.length > 0) {
				appropriateResult = sortedResults[sortedResults.length - 1];
			}

			if (appropriateResult) {
				completeResults.push({
					...appropriateResult,
					ribSpeed: headingSpeed,
					transitions: transitionsInRange,
					initialRecommendationId: initialRecommendationId,
				} as any);
			}
		});

		const lastHeaderSpeed = headingDataCopy[headingDataCopy.length - 1];
		if (completeResults.length > 0) {
			const lastResult = completeResults[completeResults.length - 1];
			completeResults.push({
				...lastResult,
				ribSpeed: `> ${ lastHeaderSpeed }`,
				recommendationId: 0,
				transitions: [],
			} as any);
		}

		fullResultsSet[key] = completeResults;
	}

	return fullResultsSet;
};

const formatMappedResults = (fullResultsSet: Record<string, BearingLubricationResult[]>, calculationTableData: string[][]) => {
	Object.keys(fullResultsSet).forEach((key) => {
		if (!fullResultsSet[key] || fullResultsSet[key].length === 0) {
			return;
		}

		const resultsLubricantId = fullResultsSet[key][0].lubricantId;
		const isSingleLubricantData = Object.values(fullResultsSet).every((resultSet) => {
			return resultSet[0].lubricantId === resultsLubricantId;
		});
		const additionalLabelText = isSingleLubricantData ? 'Lubricant System: ' : '';
		const rowLabel = `${additionalLabelText}${fullResultsSet[key][0].description}`;

		const speedRatings = fullResultsSet[key].map((item: any) => {
			if (item.initialRecommendationId !== undefined) {
				return `${item.recommendationId}:::${item.initialRecommendationId}:::${JSON.stringify(item.transitions || [])}`;
			}
			return item.recommendationId.toString();
		});

		speedRatings.unshift(rowLabel);
		calculationTableData.push(speedRatings);
	});
};

const fetchSpeedUnit = () => {
	const unitCode = (document.getElementById('display-unit') as HTMLInputElement).value;
	const speedUnit = lubricationSpeedUnitMap[ unitCode as TimkenUnit ];
	return `Rib Speed (${ speedUnit })`;
};

const mapSpeedCalculationResults = (results: BearingLubricationResult[]) => {
	const resultsCopy = structuredClone(results);
	const headingData = [ resultsCopy[ 0 ].ribSpeed ];
	headingData.unshift(fetchSpeedUnit());
	const calculationTableData: string[][] = [];
	const groupedResults = groupResultsByLubricantId(resultsCopy);
	const mappedResultsObject: CalculationState = initializeCalculationState(headingData);
	formatMappedResults(groupedResults, calculationTableData);
	mappedResultsObject.sectionData = calculationTableData.reverse();
	return mappedResultsObject;
};

const groupResultsByLubricantId = (results: BearingLubricationResult[]) => {
	return results.reduce((resultsArray, result) => {
		const key = result.lubricantId.toString();
		if (! resultsArray[ key ]) {
			resultsArray[ key ] = [];
		}
		resultsArray[ key ].push(result);
		return resultsArray;
	}, {} as Record<string, BearingLubricationResult[]>);
};

const initializeCalculationState = (headerData: string[]): CalculationState => {
	return {
		sectionHeading: headerData,
		sectionName: '',
		sectionData: [],
	};
};

const mapCalculationResults = (results: BearingLubricationResult[]) => {
	const unitCode = (document.getElementById('display-unit') as HTMLInputElement).value as TimkenUnit;
	const speedValue = (document.getElementById('operating-speed') as HTMLInputElement).value;

	if (parseCommaNumber(speedValue) > 0 && speedValue !== '') {
		return mapSpeedCalculationResults(results);
	}

	let headingData = generateDynamicHeaders(results, unitCode);
	const lastHeaderSpeed = headingData[headingData.length - 1];
	const headingDataForProcessing = ['Rib Speed (m/s)', ...headingData];

	headingData.unshift(fetchSpeedUnit());
	headingData.push(`> ${lastHeaderSpeed}`);

	const mappedResultsObject: CalculationState = initializeCalculationState(headingData);

	const calculationTableData: string[][] = [];
	const groupedResults = groupResultsByLubricantId(results);
	const fullResultsSet = buildAllSpeedRatingValues(groupedResults, headingDataForProcessing);

	formatMappedResults(fullResultsSet, calculationTableData);
	mappedResultsObject.sectionData = calculationTableData.reverse();
	return mappedResultsObject;
};

export { formReducer, calculationReducer };
